// Board vocabulary for the rules core. Pure — no React, no DOM, no I/O.
//
// Squares are a *packed coordinate*: sq = (rank + BIAS) << SHIFT | (file +
// BIAS). This replaced 0x88, which addressed an 8x8 board inside 128-entry
// typed arrays and tested "off board" with `sq & 0x88`. The board expands now,
// so neither the fixed size nor the fixed mask could stay.
//
// Two things survive the change, on purpose:
//
//   - **A square is still one integer**, so every direction offset still works
//     by addition. North is +STRIDE instead of +16, a knight is ±(2*STRIDE±1)
//     instead of ±33/±31/±18/±14, and the sliding loops in moveGen and attacks
//     are untouched apart from their bounds test. Moving to {file, rank}
//     objects would have rewritten all of it and allocated per step.
//   - **Iteration order is rank-then-file**, which on the classic board is the
//     identical a1..h8 sequence 0x88's SQUARES gave. Determinism depends on it
//     (PLAN §5.5) and so does every tie-break in the industry rules.
//
// What is genuinely gone is dense storage: the board and every industry field
// is a Map keyed by square, holding only the cells that have something in
// them. That is what makes an expanded board affordable — the per-square unit
// stores are 24 entries each, so a 200x200 dense buffer would be a million
// slots to hold a few dozen units.
//
// The packing bounds coordinates to +-512, which is not literally infinite.
// Any packing into one int is bounded; a genuinely unbounded board needs
// coordinate pairs or BigInt, and pays for it on every offset arithmetic in
// the hot path. A 1024x1024 world is more board than a game will ever explore.

export * from "src/shared/board";
import {
  BIAS,
  BISHOP,
  BLACK,
  type Bounds,
  CLASSIC_BOUNDS,
  COORD_MAX,
  COORD_MIN,
  type Direction,
  DIR_OFFSET,
  DIRECTIONS,
  EMPTY,
  FILE_MASK,
  fileOf,
  forwardFacing,
  KING,
  KNIGHT,
  makePiece,
  NORTH,
  PAWN,
  type Piece,
  pieceColor,
  pieceType,
  type PieceType,
  QUEEN,
  rankOf,
  ROOK,
  SHIFT,
  type Side,
  SOUTH,
  square,
  squaresIn,
  STRIDE,
  WEST,
  WHITE
} from "src/shared/board";

/**
 * Which army a piece belongs to. A plain number, not `0 | 1`.
 *
 * Two sides is the classic game, not an assumption the core gets to make: the
 * roguelike opens onto a board with more than one enemy on it. Everything that
 * used to mean "the other side" now means "a side that is not this one", and
 * everything that was a two-element table (forward facing, promotion rank,
 * king squares) is indexed by side instead.
 *
 * `Color` remains as an alias because "the white pieces" is what a chess player
 * says, and most of the code is still talking about a two-player game.
 */
// `Side`, `Piece` and their packing are `src/shared/board.ts`, re-exported above.

export type PieceClass = 0 | 1 | 2 | 3;
export const CLASS_OTHER: PieceClass = 0;
export const CLASS_FODDER: PieceClass = 1;
export const CLASS_POWER: PieceClass = 2;
export const CLASS_MONARCH: PieceClass = 3;

/** Every rank's class. */
export const PIECE_CLASS: Record<PieceType, PieceClass> = {
  [PAWN]: CLASS_FODDER,
  [KNIGHT]: CLASS_POWER,
  [BISHOP]: CLASS_POWER,
  [ROOK]: CLASS_POWER,
  [QUEEN]: CLASS_POWER,
  [KING]: CLASS_MONARCH
};

/** A piece's class; `null` for an empty square, which has no piece to classify. */
export const pieceClass = (p: Piece): PieceClass | null =>
  p === EMPTY ? null : PIECE_CLASS[pieceType(p)];

/**
 * The other side, in a two-sided game.
 *
 * Kept for the places that genuinely mean "the two-player opponent" — seat
 * assignment, the hot-seat camera. It is **not** how enemies are identified in
 * the rules: `x ^ 1` maps side 2 to side 3, which is a different army rather
 * than a wrong answer, and that is the kind of bug that never throws. Use
 * `isEnemy`, or the `EnemyOf` attack query.
 */
export const opposite = (c: Side): Side => c ^ 1;

/** Anyone who is not you. The only definition that survives a third army. */
export const isEnemy = (a: Side, b: Side): boolean => a !== b;

// Packed coordinates — `square`, `fileOf`, `rankOf`, `STRIDE` — are `src/shared/board.ts`.

/**
 * The bounding rectangle of the board. Inclusive on all four sides.
 *
 * **Derived, not authoritative.** The board is a *set of squares* and does not
 * have to be a rectangle — an expansion opens a lobe here and a corridor
 * there, and the holes between them are genuinely off the board. This is the
 * box around that set, kept for the things that legitimately want one: camera
 * limits, and sizing a sweep.
 */
// `Bounds` is `src/shared/board.ts`.


export const cloneBounds = (b: Bounds): Bounds => ({ ...b });

export const boundsEqual = (a: Bounds, b: Bounds): boolean =>
  a.minFile === b.minFile &&
  a.maxFile === b.maxFile &&
  a.minRank === b.minRank &&
  a.maxRank === b.maxRank;

export const boundsWidth = (b: Bounds): number => b.maxFile - b.minFile + 1;
export const boundsHeight = (b: Bounds): number => b.maxRank - b.minRank + 1;

/**
 * Is this square on the board?
 *
 * Two comparisons per axis where 0x88 needed one mask, which is the price of a
 * board whose size is not a compile-time constant.
 *
 * Leave it as a plain module function. Hoisting the four fields into locals and
 * closing over them inside the hot callers — which reads like the obvious
 * optimisation — made kiwipete perft *46% slower* (8.0s to 11.7s): a fresh
 * closure per call is worse than a monomorphic function V8 can inline.
 */
export function inBounds(b: Bounds, sq: number): boolean {
  const file = (sq & FILE_MASK) - BIAS;
  if (file < b.minFile || file > b.maxFile) return false;
  const rank = (sq >>> SHIFT) - BIAS;
  return rank >= b.minRank && rank <= b.maxRank;
}

/**
 * Every square of a rectangle, **rank then file** — a1, b1, ... h1, a2, ...
 *
 * The order is load-bearing, not incidental: it is what `SQUARES` gave before
 * the board could grow, and a pile of industry rules break ties by "first in
 * board order". Two clients derive state independently from one log, so an
 * unordered sweep is a desync (PLAN §5.5).
 *
 * For "every square of the *board*" use `position.cellList`, which is the same
 * order over the squares that actually exist.
 */
// `squaresIn` is `src/shared/board.ts`.

/** The box around a set of squares. Empty set gives an empty box at the origin. */
export function boundsOf(cells: Iterable<number>): Bounds {
  let minFile = Infinity;
  let maxFile = -Infinity;
  let minRank = Infinity;
  let maxRank = -Infinity;
  for (const sq of cells) {
    const file = fileOf(sq);
    const rank = rankOf(sq);
    if (file < minFile) minFile = file;
    if (file > maxFile) maxFile = file;
    if (rank < minRank) minRank = rank;
    if (rank > maxRank) maxRank = rank;
  }
  if (minFile === Infinity) return { minFile: 0, maxFile: -1, minRank: 0, maxRank: -1 };
  return { minFile, maxFile, minRank, maxRank };
}


/** Grow the board to include `sq`, returning a new Bounds. */
export function boundsWith(b: Bounds, sq: number): Bounds {
  const file = fileOf(sq);
  const rank = rankOf(sq);
  return {
    minFile: Math.min(b.minFile, file),
    maxFile: Math.max(b.maxFile, file),
    minRank: Math.min(b.minRank, rank),
    maxRank: Math.max(b.maxRank, rank)
  };
}

/** Grow the board outward by `margin` on every side, clamped to the packing. */
export function grownBy(b: Bounds, margin: number): Bounds {
  return {
    minFile: Math.max(COORD_MIN, b.minFile - margin),
    maxFile: Math.min(COORD_MAX, b.maxFile + margin),
    minRank: Math.max(COORD_MIN, b.minRank - margin),
    maxRank: Math.min(COORD_MAX, b.maxRank + margin)
  };
}

/**
 * The squares a board padded `margin` out from the classic sixty-four holds.
 *
 * Here rather than beside the scenario table it was written for, because the
 * *rules* now open boards of this shape (`maps.ts`) and `src/engine/rules/` may not
 * import from `src/engine/ai/`. One definition on purpose: two answers to "which
 * squares does a board padded two out have" is a scenario and a map that look
 * identical and are not.
 */
export const paddedCells = (margin: number): number[] =>
  squaresIn(grownBy(CLASSIC_BOUNDS, margin));

export const A1 = square(0, 0);
export const E1 = square(4, 0);
export const H1 = square(7, 0);
export const A8 = square(0, 7);
export const E8 = square(4, 7);
export const H8 = square(7, 7);

export const CASTLE_WK = 1;
export const CASTLE_WQ = 2;
export const CASTLE_BK = 4;
export const CASTLE_BQ = 8;
export const CASTLE_ALL = CASTLE_WK | CASTLE_WQ | CASTLE_BK | CASTLE_BQ;

/**
 * Castling rights survive a move unless its origin or destination touches a
 * king or corner square. `castling &= mask(from) & mask(to)` handles every
 * case at once, including a rook being captured on its home square.
 *
 * A lookup table over six squares rather than an array over the whole board:
 * the board has no fixed size to allocate one for any more.
 */
const CASTLE_MASKS = new Map<number, number>([
  [A1, CASTLE_ALL & ~CASTLE_WQ],
  [H1, CASTLE_ALL & ~CASTLE_WK],
  [E1, CASTLE_ALL & ~(CASTLE_WK | CASTLE_WQ)],
  [A8, CASTLE_ALL & ~CASTLE_BQ],
  [H8, CASTLE_ALL & ~CASTLE_BK],
  [E8, CASTLE_ALL & ~(CASTLE_BK | CASTLE_BQ)]
]);

export const castleRightsMask = (sq: number): number => CASTLE_MASKS.get(sq) ?? CASTLE_ALL;

// Direction offsets. Diagonals first so QUEEN_DIRS can concatenate cleanly.
export const BISHOP_DIRS = [STRIDE + 1, STRIDE - 1, -STRIDE + 1, -STRIDE - 1] as const;
export const ROOK_DIRS = [STRIDE, 1, -1, -STRIDE] as const;
export const QUEEN_DIRS = [...BISHOP_DIRS, ...ROOK_DIRS] as const;
export const KING_DIRS = QUEEN_DIRS;

/**
 * Chebyshev radius a knight re-aims (and a Paladin rallies) across (§31 P1:
 * the doc's "3 square radius for turning Pawns"). Was a knight's move or
 * touching — both sit inside it.
 */
export const REAIM_RADIUS = 3;

/**
 * What a knight can reach without moving: any square within `REAIM_RADIUS`.
 *
 * One function, because "what can this knight reach" must have exactly one
 * answer — re-aim, rally and the board's highlight ask it, and a highlight
 * that disagreed would offer a target the reducer refuses. Coordinates, not
 * packed offsets: an offset wraps at the file band.
 */
export function knightReaches(from: number, to: number, includingSelf = false): boolean {
  if (from === to) return includingSelf;
  return (
    Math.max(Math.abs(fileOf(from) - fileOf(to)), Math.abs(rankOf(from) - rankOf(to))) <=
    REAIM_RADIUS
  );
}
export const KNIGHT_DIRS = [
  2 * STRIDE + 1,
  2 * STRIDE - 1,
  STRIDE + 2,
  STRIDE - 2,
  -STRIDE + 2,
  -STRIDE - 2,
  -2 * STRIDE + 1,
  -2 * STRIDE - 1
] as const;

// --- facing ---------------------------------------------------------------
//
// Facing lives in Position rather than in the industry state because it
// governs where a pawn *moves* and *captures*, which means check detection
// depends on it. A rotated enemy pawn attacks different squares, so it is
// chess state whatever else it also does.

// `Direction`, the four headings, `DIR_OFFSET` and `forwardFacing` are `src/shared/board.ts`.

/**
 * A pawn captures on the two diagonals flanking its facing: the facing offset
 * plus each perpendicular. For north that recovers the familiar +STRIDE±1.
 */
export const PAWN_CAPTURES_BY_FACING: readonly (readonly number[])[] = DIRECTIONS.map(
  (facing) => [
    DIR_OFFSET[facing] + DIR_OFFSET[(facing + 1) % 4],
    DIR_OFFSET[facing] + DIR_OFFSET[(facing + 3) % 4]
  ]
);

/**
 * A **facing-relative** step, as a board offset.
 *
 * `forward` is along the facing and `right` is one quarter-turn clockwise from
 * it, so a step is written the way the piece experiences it and the four
 * facings fall out of one row rather than four tables that must agree.
 * `stepOffset(NORTH, 1, 1)` is the north-east diagonal, which is what
 * `PAWN_CAPTURES_BY_FACING` already spells out the long way — and
 * `coords.test.ts` pins the two against each other rather than against a
 * constant either of them owns.
 *
 * **Offsets add**, which is the whole reason a square is a packed coordinate:
 * a step of two forward is twice the offset and needs no second table.
 */
export const stepOffset = (facing: Direction, forward: number, right: number): number =>
  DIR_OFFSET[facing] * forward + DIR_OFFSET[(facing + 1) % 4] * right;

/**
 * Rank a pawn promotes *on*, by colour — never a function of facing (PLAN
 * §3.4). Only meaningful on the classic board: once the board has expanded
 * there is no enemy back rank to reach, and promotion moves to a condition of
 * its own (see `promotionRankFor`).
 */
export const PROMOTION_RANK = [7, 0] as const;
/** Rank a pawn starts on, which is also where the double step is available. */
export const PAWN_START_RANK = [1, 6] as const;

/**
 * Where a side's pawns start, or null for a side that has no home rank.
 *
 * Only the two classic armies have one. An army that arrived in a wave has no
 * starting rank and therefore no double step — which is right: the double step
 * is a courtesy extended to a piece that has not moved yet from its home, and
 * a reinforcement has no home to be on.
 */
export const pawnStartRank = (side: Side): number | null =>
  side < PAWN_START_RANK.length ? PAWN_START_RANK[side] : null;

/** How wide the board chess is played on is. Derived, so it cannot drift. */
export const CLASSIC_WIDTH = CLASSIC_BOUNDS.maxFile - CLASSIC_BOUNDS.minFile + 1;

/**
 * The far edge a side's pawns promote against.
 *
 * `facing` is the direction that side *marches*, and `line` is the rank (for
 * north and south) or file (for west and east) that edge stands on.
 */
export interface PromotionWall {
  facing: Direction;
  line: number;
}

/**
 * The wall a pawn of `side` promotes against, or null when there is none.
 *
 * **A property of the side, never of the pawn's facing** (PLAN §3.4). That is
 * the load-bearing half: tie promotion to where a pawn happens to be pointing
 * and a rook can mint a south-facing pawn two squares from its own back rank,
 * which is an infinite queen machine. A side marches one way — `forwardFacing`
 * — and the wall it is walking at is the one it promotes on, whatever any
 * individual pawn has since been re-aimed to.
 *
 * On the classic board this is exactly the classic rule: White's forward is
 * north and the far rank is 7, which is the enemy back rank. Perft cannot see
 * the difference, and that is the point.
 *
 * Past it, the condition is **square, whole, and bigger than the board chess
 * is played on**:
 *
 * - **Square**, because "the opposite wall" is only a fair thing to ask of an
 *   army when every army has the same distance to walk.
 * - **Whole** — no holes, `cells` exactly filling the box — because a board
 *   that has grown into a ragged shape has no wall to speak of, only a
 *   frontier, and a pawn promoting against whatever the frontier happened to
 *   be that stage is the failure the old rule switched itself off to avoid.
 *   A run's board is exactly that shape, and it keeps `Position.promotion`,
 *   the squares a pawn can be *sent* to.
 * - **Bigger**, because a small board's wall is a few steps away and the whole
 *   reason a march is worth something is that it is long.
 *
 * On the classic board only the two classic armies have a wall — nobody else
 * has an enemy back rank. Past it every side does, since the condition is the
 * board's shape rather than anybody's home.
 *
 * **The classic board is exempt from the "whole" test**, and deliberately: a
 * structure takes its square out of `cells` without moving the bounds, so
 * applying it there would mean building one warehouse in an ordinary game
 * switched promotion off for the rest of it. The test is about a board that
 * *grew* into a shape, not about a hole somebody built.
 */
export function promotionWall(
  bounds: Bounds,
  /** How many squares the board actually holds, for the "no holes" test. */
  cells: number,
  side: Side
): PromotionWall | null {
  const width = bounds.maxFile - bounds.minFile + 1;
  const height = bounds.maxRank - bounds.minRank + 1;
  const classic = boundsEqual(bounds, CLASSIC_BOUNDS);
  const walled = width === height && cells === width * height && width > CLASSIC_WIDTH;
  if (!classic && !walled) return null;
  if (classic && side >= PROMOTION_RANK.length) return null;

  const facing = forwardFacing(side);
  const line =
    facing === NORTH
      ? bounds.maxRank
      : facing === SOUTH
        ? bounds.minRank
        : facing === WEST
          ? bounds.minFile
          : bounds.maxFile;
  return { facing, line };
}

/** The coordinate of `sq` on the axis a wall runs across. */
export const wallCoordOf = (wall: PromotionWall, sq: number): number =>
  wall.facing === NORTH || wall.facing === SOUTH ? rankOf(sq) : fileOf(sq);

/** How many steps a pawn on `sq` still has to walk to reach `wall`. */
export const stepsToWall = (wall: PromotionWall, sq: number): number =>
  Math.abs(wall.line - wallCoordOf(wall, sq));

// `squareName` and `parseSquare` are `src/shared/board.ts`.

export const PIECE_LETTERS = ".pnbrqk";

export function pieceToChar(p: Piece): string {
  const letter = PIECE_LETTERS[pieceType(p)];
  return pieceColor(p) === WHITE ? letter.toUpperCase() : letter;
}

export function charToPiece(ch: string): Piece {
  const type = PIECE_LETTERS.indexOf(ch.toLowerCase()) as PieceType;
  if (type <= 0) throw new Error(`bad piece: ${ch}`);
  return makePiece(ch === ch.toUpperCase() ? WHITE : BLACK, type);
}
